/**
 * API Helper utilities for consistent fetch handling
 * 
 * Prevents "body stream already read" errors and guarantees error message surfacing.
 * NEVER shows "Unknown error" when we have actual response data.
 */

/**
 * Safely read the response body once and parse if JSON.
 * Guaranteed not to throw - always returns a result object.
 * 
 * @param {Response} response - Fetch Response object
 * @returns {Promise<{data: Object|null, raw: string}>} - Parsed data and raw text
 */
export async function safeReadBody(response) {
  const result = { data: null, raw: "" };
  
  try {
    const contentType = response.headers.get("content-type") || "";
    
    // Try to clone first (safest approach)
    let text = "";
    try {
      const cloned = response.clone();
      text = await cloned.text();
    } catch {
      // Clone failed, try original
      try {
        text = await response.text();
      } catch {
        // Both failed - return empty
        return result;
      }
    }
    
    result.raw = text;
    
    if (!text || text.trim() === "") {
      return result;
    }
    
    // Parse as JSON if content-type indicates JSON
    if (contentType.includes("application/json")) {
      try {
        result.data = JSON.parse(text);
      } catch {
        // JSON parse failed - raw text is still available
      }
    } else {
      // Try parsing anyway in case content-type is wrong
      try {
        result.data = JSON.parse(text);
      } catch {
        // Not JSON - that's fine, raw text is available
      }
    }
  } catch (error) {
    console.error("[safeReadBody] Unexpected error:", error);
  }
  
  return result;
}

/**
 * Format a 422 validation error from FastAPI/Pydantic format.
 * Handles array of {loc, msg, type} validation errors.
 * 
 * @param {Object} data - Parsed response data
 * @param {string} rawText - Raw response text as fallback
 * @returns {string} - Formatted validation error message
 */
export function format422Detail(data, rawText) {
  if (!data) {
    return rawText?.slice(0, 300) || "Validation error";
  }
  
  // Check if this is a warning (business hours) - return marker
  if (data.detail?.type === "outside_business_hours") {
    return "WARNING_422";
  }
  
  const detail = data.detail;
  
  // String detail - return as-is
  if (typeof detail === "string") {
    return detail;
  }
  
  // Array of validation errors (FastAPI/Pydantic format)
  if (Array.isArray(detail)) {
    return detail.map(e => {
      const loc = Array.isArray(e.loc) ? e.loc.join(".") : String(e.loc || "field");
      const msg = e.msg || JSON.stringify(e);
      return `${loc}: ${msg}`;
    }).join(" | ");
  }
  
  // Object detail - try to extract message
  if (typeof detail === "object" && detail !== null) {
    if (detail.message) return detail.message;
    if (detail.msg) return detail.msg;
    return JSON.stringify(detail).slice(0, 300);
  }
  
  // Fallback to stringified data
  return JSON.stringify(data).slice(0, 300);
}

/**
 * Extract the best available error message from a response.
 * NEVER returns "Unknown error" if we have any response data.
 * 
 * @param {Response} response - Original fetch Response
 * @param {{data: Object|null, raw: string}} parsed - Result from safeReadBody
 * @returns {string} - Human-readable error message with HTTP status
 */
export function bestErrorMessage(response, parsed) {
  // Special handling for 422 - use format422Detail
  if (response.status === 422) {
    const formatted = format422Detail(parsed?.data, parsed?.raw);
    if (formatted === "WARNING_422") {
      // This shouldn't be shown as error - it's a warning
      return "Outside business hours warning";
    }
    return `Validation error (422): ${formatted}`;
  }
  
  const d = parsed?.data;
  
  // Try various common error message locations
  const message = 
    // FastAPI validation error format
    (d?.detail && typeof d.detail === "string" && d.detail) ||
    // FastAPI validation error array format
    (Array.isArray(d?.detail) && d.detail[0]?.msg) ||
    // Object detail with message/msg
    (d?.detail && typeof d.detail === "object" && (d.detail.message || d.detail.msg)) ||
    // Standard message fields
    d?.message ||
    d?.msg ||
    d?.error ||
    // Raw text (truncated)
    (parsed?.raw && parsed.raw.slice(0, 300)) ||
    // Fallback to HTTP status only
    null;
  
  if (message) {
    return `Error (${response.status}): ${message}`;
  }
  
  return `HTTP ${response.status} ${response.statusText || "Error"}`;
}

/**
 * Check if response data indicates an outside business hours warning.
 * 
 * @param {{data: Object|null, raw: string}} parsed - Result from safeReadBody
 * @returns {boolean}
 */
export function isOutsideHoursWarning(parsed) {
  const d = parsed?.data;
  if (!d || !d.detail) return false;
  return d.detail.type === "outside_business_hours";
}

/**
 * Legacy function for backwards compatibility.
 * @deprecated Use safeReadBody instead
 */
export async function safeReadJson(response) {
  const { data } = await safeReadBody(response);
  return data;
}

/**
 * Legacy function for backwards compatibility.
 * @deprecated Use bestErrorMessage instead
 */
export function extractErrorMessage(data) {
  if (!data) return "No response data";
  
  if (data.detail) {
    if (typeof data.detail === "string") return data.detail;
    if (Array.isArray(data.detail) && data.detail[0]?.msg) return data.detail[0].msg;
    if (typeof data.detail === "object") {
      return data.detail.message || data.detail.msg || JSON.stringify(data.detail);
    }
  }
  
  if (data.message) return data.message;
  if (data.msg) return data.msg;
  if (data.error) return data.error;
  if (data.raw) return data.raw.slice(0, 300);
  
  return "No error details available";
}
